import type { FC } from "react"
import { useCallback, useMemo } from "react"

import SearchSuggestion from "@/components/SearchSuggestion"
import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import { useGetPurchaseOrderSuggestions } from "@/screens/WarehouseReceiving/subfeatures/Incoming/hooks"
import { cleanPurchaseOrderSearchValue } from "@/screens/WarehouseReceiving/subfeatures/Incoming/utils"
import type { AutocompleteOption } from "@/types/form-fields"
import useDebounce from "@/utils/useDebounce"

interface PurchaseOrderSearchSuggestionProps {
  inputValue: string
  onSearch: (searchedItem: string | AutocompleteOption) => void
  onInputChange: (value: string) => void
}

export const PurchaseOrderSearchSuggestion: FC<PurchaseOrderSearchSuggestionProps> = ({
  inputValue,
  onSearch,
  onInputChange,
}) => {
  const debouncedValue = useDebounce(inputValue, DEBOUNCE_DELAY_MS)

  const handleSetInputValue = useCallback(
    (value: string) => {
      const cleaned = cleanPurchaseOrderSearchValue(value)
      onInputChange(cleaned)
    },
    [onInputChange]
  )

  const shouldQuery = !!debouncedValue?.length
  const { purchaseOrders, isFetching } = useGetPurchaseOrderSuggestions({
    query: shouldQuery ? debouncedValue : "",
  })

  const options = useMemo(() => {
    if (isFetching) return []

    return (purchaseOrders?.map((purchaseOrder) => ({
      label: `PO# ${purchaseOrder?.orderId}${purchaseOrder?.as400OrderNumber ? ` / ${purchaseOrder?.as400OrderNumber}` : ""}`,
      id: purchaseOrder?.orderId,
    })) || []) as AutocompleteOption[]
  }, [purchaseOrders, isFetching])

  return (
    <SearchSuggestion
      onSearch={onSearch}
      placeholder='Search by PO Number or Container/Tracking ID'
      inputValue={inputValue}
      setInputValue={handleSetInputValue}
      options={options}
      fetching={isFetching}
    />
  )
}
